import { useEffect, useState } from 'react';

// Pacer de reveal côté affichage (même architecture que le RevealEngine iOS 30 Hz
// et le plan pacer Android) : le texte reçu est bufferisé et n'entre dans le DOM
// qu'à cadence bornée et MONOTONE. C'est ce qui garantit l'ordre visuel : un
// stagger CSS ne peut pas le faire (délai = index × stagger depuis le mount ⇒
// la queue d'un batch apparaît après la tête du batch suivant dès que le débit
// dépasse tick/stagger — inversions « texte à trous » constatées en QA).
export const TICK_MS = 33; // ~30 Hz, aligné sur l'existant iOS
// Résorbe le backlog en ~10 ticks (~330 ms) : assez lent pour lisser les rafales
// SSE du H100, assez rapide pour ne jamais sembler à la traîne.
const CATCH_UP_TICKS = 10;
const MIN_CHARS_PER_TICK = 4;
const MAX_CHARS_PER_TICK = 160;
// Drain accéléré une fois le stream terminé, pour un petit reliquat de tokens.
const DRAIN_MULTIPLIER = 4;

// Bloc : un saut de texte reçu d'un coup (réponse relue avant affichage, texte
// de secours). Il est révélé au rythme de lecture sur une durée bornée, que le
// flux soit encore ouvert ou déjà terminé : ~2 s pour 2 000 caractères, jamais
// plus de 2,5 s. Le streaming token par token n'atteint pas ce seuil par mise à jour.
export const BLOCK_MIN_CHARS = 400;
const BLOCK_READING_CHARS_PER_SECOND = 1_000;
export const MAX_BLOCK_REVEAL_MS = 2_500;

export type RevealState = {
  /** Linear pace of the block being revealed, null outside a block. */
  blockCharsPerTick: number | null;
  /** Text length seen at the previous tick, to detect a block arrival. */
  lastTotal: number;
  revealed: number;
};

export function computeNextRevealLength(
  revealedLength: number,
  totalLength: number,
  isStreaming: boolean,
) {
  if (totalLength <= revealedLength) {
    // Texte raccourci (reset/regénération) : resynchronisation immédiate.
    return totalLength;
  }
  const backlog = totalLength - revealedLength;
  const baseStep = Math.min(
    MAX_CHARS_PER_TICK,
    Math.max(MIN_CHARS_PER_TICK, Math.ceil(backlog / CATCH_UP_TICKS)),
  );
  const step = isStreaming ? baseStep : baseStep * DRAIN_MULTIPLIER;
  return Math.min(totalLength, revealedLength + step);
}

/** Linear pace revealing `backlog` characters at reading speed, within the time bound. */
export function computeBlockCharsPerTick(backlog: number) {
  const durationMs = Math.min(MAX_BLOCK_REVEAL_MS, (backlog / BLOCK_READING_CHARS_PER_SECOND) * 1_000);
  return Math.max(MIN_CHARS_PER_TICK, Math.ceil((backlog * TICK_MS) / Math.max(TICK_MS, durationMs)));
}

export function stepReveal(state: RevealState, totalLength: number, isStreaming: boolean): RevealState {
  if (totalLength <= state.revealed) {
    return { blockCharsPerTick: null, lastTotal: totalLength, revealed: totalLength };
  }
  const backlog = totalLength - state.revealed;
  let blockCharsPerTick = state.blockCharsPerTick;
  if (totalLength - state.lastTotal >= BLOCK_MIN_CHARS) {
    blockCharsPerTick = Math.max(blockCharsPerTick ?? 0, computeBlockCharsPerTick(backlog));
  } else if (blockCharsPerTick !== null) {
    // Text kept flowing behind the block: the backlog still drains within the bound.
    blockCharsPerTick = Math.max(blockCharsPerTick, Math.ceil((backlog * TICK_MS) / MAX_BLOCK_REVEAL_MS));
  }
  const revealed = blockCharsPerTick === null
    ? computeNextRevealLength(state.revealed, totalLength, isStreaming)
    : Math.min(totalLength, state.revealed + blockCharsPerTick);
  return {
    blockCharsPerTick: revealed >= totalLength ? null : blockCharsPerTick,
    lastTotal: totalLength,
    revealed,
  };
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useStreamRevealText(fullText: string, isStreaming: boolean) {
  // Un message d'historique (jamais streamé) est révélé d'emblée : aucun pacer,
  // aucun re-render — le coût est strictement limité au message en cours.
  const [state, setState] = useState<RevealState>(() => {
    const revealed = isStreaming ? 0 : fullText.length;
    return { blockCharsPerTick: null, lastTotal: revealed, revealed };
  });
  // Latch « ce message a streamé » via ajustement d'état en render (pattern
  // React documenté) : le drain du pacer doit survivre à isStreaming → false.
  const [hasStreamed, setHasStreamed] = useState(isStreaming);
  if (isStreaming && !hasStreamed) {
    setHasStreamed(true);
  }

  const isRevealing = hasStreamed && state.revealed < fullText.length;

  useEffect(() => {
    if (!isRevealing) {
      return;
    }

    let rafId = 0;
    let lastTickMs = performance.now();
    const tick = (nowMs: number) => {
      if (nowMs - lastTickMs >= TICK_MS) {
        lastTickMs = nowMs;
        setState((current) => (
          prefersReducedMotion()
            ? { blockCharsPerTick: null, lastTotal: fullText.length, revealed: fullText.length }
            : stepReveal(current, fullText.length, isStreaming)
        ));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [fullText.length, isRevealing, isStreaming]);

  // Resynchronisation si le texte a raccourci (reset) hors boucle de reveal.
  const boundedLength = Math.min(state.revealed, fullText.length);

  return {
    isRevealing,
    text: hasStreamed ? fullText.slice(0, boundedLength) : fullText,
  };
}
